import { addDays } from 'date-fns';
import { createDateSafe, getBestDate } from '@utils/dateUtils';
import type { DocumentListItem } from '@hooks/useDocumentsQuery';
import type { DocumentFilters } from '@hooks/useDocumentsQuery';

type CategoryDocument = Pick<DocumentListItem, 'category'> & { categoryName?: string | null };

export const getCategoryCode = (doc: CategoryDocument): string => {
  const { category } = doc;
  if (typeof category === 'string') return category;
  if (category && typeof category === 'object') {
    return category.code || category.id || category.name || '';
  }
  return '';
};

export const getCategoryLabel = (doc: CategoryDocument): string =>
  doc.categoryName ||
  (typeof doc.category === 'object' && doc.category?.name) ||
  getCategoryCode(doc);

export const getFileType = (doc: DocumentListItem): string => doc.fileType || '';

export const getFileSize = (doc: DocumentListItem): number => doc.size || 0;

const matchesSearch = (doc: DocumentListItem, query: string): boolean => {
  const needle = query.toLowerCase();
  return (
    doc.title?.toLowerCase().includes(needle) ||
    (doc.description?.toLowerCase().includes(needle) ?? false) ||
    (doc.tags?.some((tag: string) => tag.toLowerCase().includes(needle)) ?? false)
  );
};

const matchesDateRange = (doc: DocumentListItem, range: DocumentFilters['dateRange']): boolean => {
  if (!range.start && !range.end) return true;
  const date = createDateSafe(getBestDate(doc));
  if (!date) return true;
  if (range.start && date < range.start) return false;
  if (range.end && date >= addDays(range.end, 1)) return false;
  return true;
};

export function filterDocuments(
  documents: DocumentListItem[],
  searchQuery: string,
  filters?: DocumentFilters,
): DocumentListItem[] {
  return documents.filter((doc) => {
    if (searchQuery && !matchesSearch(doc, searchQuery)) return false;
    if (!filters) return true;

    if (filters.categories.length > 0 && !filters.categories.includes(getCategoryCode(doc))) {
      return false;
    }
    if (filters.tags.length > 0 && !doc.tags?.some((tag: string) => filters.tags.includes(tag))) {
      return false;
    }
    if (filters.fileTypes.length > 0 && !filters.fileTypes.includes(getFileType(doc))) {
      return false;
    }
    if (filters.uploadedBy.length > 0 && !filters.uploadedBy.includes(doc.uploadedBy)) {
      return false;
    }
    return matchesDateRange(doc, filters.dateRange);
  });
}
